'use client'

import { useMemo, useEffect } from 'react'

import { useSession } from 'next-auth/react'
import qs from 'qs'

import {
  useQuery,
  UseQueryOptions,
  QueryKey,
  DefaultError,
  useQueryClient
} from '@tanstack/react-query'

import { useData } from '@/shared/contexts/DataContext'
import { useCurrentUser } from '@/shared/hooks/useCurrentUser'
import { api } from '@/shared/services/api'

type Primitive = string | number | boolean | null | undefined
type QueryParamValue =
  | Primitive
  | QueryParamValue[]
  | { [key: string]: QueryParamValue }
export type ApiParams = Record<string, QueryParamValue> | undefined

type Paginated<T> = { records: T[]; pagination?: unknown }
type Single<T> = { record: T }

export interface UseOptimizedQueryOptions<
  TData,
  TError = DefaultError
> extends Omit<
  UseQueryOptions<TData, TError>,
  'queryKey' | 'queryFn' | 'enabled'
> {
  path: string
  params?: ApiParams
  enabled?: boolean
  initialDataOverride?: TData
  skipInitialData?: boolean
  usePlaceholder?: boolean
}

function deepNormalize(value: QueryParamValue): QueryParamValue {
  if (Array.isArray(value)) {
    return value.map(deepNormalize)
  }
  if (value && typeof value === 'object') {
    const obj = value as Record<string, QueryParamValue>
    const sortedEntries = Object.keys(obj)
      .sort((a, b) => a.localeCompare(b))
      .map(k => [k, deepNormalize(obj[k])])
    return Object.fromEntries(sortedEntries)
  }
  return value
}

function normalizeParams(params?: ApiParams) {
  if (!params) return undefined
  return deepNormalize(params) as ApiParams
}

function buildQueryString(params?: ApiParams) {
  if (!params) return ''
  const s = qs.stringify(params, { arrayFormat: 'brackets' })
  return s ? `?${s}` : ''
}

function useAutomaticInitialData<TData>(
  path: string,
  options: { override?: TData; skip?: boolean }
): { initialData?: TData } {
  const { getInitialData } = useData()
  if (options.skip) return {}

  if (options.override !== undefined) {
    return { initialData: options.override }
  }

  const data = getInitialData(path)
  if (data) return { initialData: data }

  const pathWithoutId = path.replace(/\/[^/]+$/, '')
  const dataWithoutId = getInitialData(pathWithoutId)
  if (dataWithoutId) return { initialData: dataWithoutId }

  return {}
}

function isRetryableAxiosError(err: any) {
  const status: number | undefined = err?.response?.status
  const code: string | undefined = err?.code
  const message: string | undefined = err?.message

  if (status === 401 || status === 403) return false
  if (status && status >= 400 && status < 500) return false

  if (code === 'ECONNABORTED') return true
  if (!status && /Network Error/i.test(message ?? '')) return true

  return status ? status >= 500 : false
}

const FIVE_MIN = 5 * 60 * 1000

export function useOptimizedQuery<TData, TError = DefaultError>({
  path,
  params,
  enabled = true,
  initialDataOverride,
  skipInitialData = false,
  staleTime = FIVE_MIN,
  gcTime = 2 * 60 * 60 * 1000,
  maxAge = FIVE_MIN,
  refetchOnWindowFocus = true,
  refetchOnReconnect = 'always',
  retry = (failureCount, error) =>
    isRetryableAxiosError(error) && failureCount < 3,
  ...options
}: UseOptimizedQueryOptions<TData, TError> & {
  maxAge?: number
}) {
  const { data: session, status } = useSession()
  const isAuthenticated = status === 'authenticated' && !!session?.accessToken
  const queryClient = useQueryClient()

  const { isAdmin } = useCurrentUser()

  const effectiveStaleTime = isAdmin ? 0 : staleTime
  const effectiveRefetchInterval = isAdmin ? false : maxAge
  const effectiveRefetchOnMount = isAdmin ? 'always' : true

  const stableParams = useMemo(() => normalizeParams(params), [params])

  const queryKey: QueryKey = useMemo(
    () => [path, stableParams],
    [path, stableParams]
  )

  const initialBits = useAutomaticInitialData<TData>(path, {
    override: initialDataOverride,
    skip: skipInitialData
  })

  useEffect(() => {
    if (!initialBits.initialData) return

    const existing = queryClient.getQueryData<TData>(queryKey)
    if (existing !== undefined) return

    queryClient.setQueryData(queryKey, initialBits.initialData, {
      updatedAt: Date.now()
    })
  }, [queryClient, queryKey, initialBits.initialData])

  const finalEnabled = Boolean(isAuthenticated && enabled)

  return useQuery<TData, TError>({
    queryKey,
    enabled: finalEnabled,
    queryFn: async () => {
      const url = `${path}${buildQueryString(stableParams)}`
      const { data } = await api.get<TData>(url)
      return data
    },

    staleTime: effectiveStaleTime,
    refetchInterval: effectiveRefetchInterval,
    refetchIntervalInBackground: !isAdmin,
    refetchOnWindowFocus: true,
    refetchOnMount: effectiveRefetchOnMount,

    placeholderData: previous => previous,

    gcTime,
    refetchOnReconnect,
    retry,
    ...options
  })
}

export function useOptimizedRecords<TRecord = unknown, TError = DefaultError>(
  opts: UseOptimizedQueryOptions<Paginated<TRecord>, TError>
) {
  const query = useOptimizedQuery<Paginated<TRecord>, TError>(opts)
  const data = query.data
  const records = data?.records ?? []
  const pagination = data?.pagination
  const isEmpty = query.isSuccess && (!records || records.length === 0)
  return { ...query, records, pagination, isEmpty }
}

export function useOptimizedRecord<TRecord = unknown, TError = DefaultError>(
  opts: UseOptimizedQueryOptions<Single<TRecord>, TError>
) {
  const query = useOptimizedQuery<Single<TRecord>, TError>(opts)
  const record = query.data?.record ?? null
  return { ...query, record }
}
